import { Application } from '@frontend/functionality/types';
import { useModals } from '@frontend/modal';
import { ReportTemplateClient } from '@frontend/report/api';
import { ReportApplicationTemplateResponse } from '@frontend/report/types';
import { refresh, useAccountRepository, useLicenseRepository } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useEffect, useMemo, useState } from 'react';
import { Location, NavigateFunction, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/store';

const SUPPORTED_REPORT_APPLICATIONS = [
    Application.CATALOGUE,
    Application.LAUNDRY,
    Application.LENDING,
    Application.NIVALIZ,
    Application.NOTIFICATIONS,
    Application.PUDO,
    Application.USERS,
    Application.VENDING
] as const;

export interface ViewProps {
    currentAccount: string;
    dispatch: ThunkDispatch<any, any, Action>;
    navigate: NavigateFunction;
    location: Location;
    tabs: string[];
    showTabs: boolean;
    activeTab: string | null;
    onChange: (tab: string) => void;
    reportApplicationTemplate: ReportApplicationTemplateResponse | null;
    reportListId: string;
    openModal: ReturnType<typeof useModals>['openModal'];
    closeModal: ReturnType<typeof useModals>['closeModal'];
    createReportId: string;
    isReady: boolean;
}

const useReportsOverview = (): ViewProps => {
    const { currentAccount } = useAccountRepository();
    const { functionalities } = useLicenseRepository();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { openModal, closeModal } = useModals();
    const reportListId = 'report-overview-list';
    const createReportId = 'create-report-modal';
    const [reportApplicationTemplate, setReportApplicationTemplate] = useState<ReportApplicationTemplateResponse | null>(null);

    const tabs = useMemo((): string[] => {
        const applications = new Set(functionalities.map((functionality) => functionality.application));
        return SUPPORTED_REPORT_APPLICATIONS.filter((application) => applications.has(application));
    }, [functionalities]);

    const showTabs = tabs.length > 1;
    const activeTabFromParams = searchParams.get('report-tag');
    const activeTab = useMemo(() => {
        if (tabs.length === 0) return null;
        if (activeTabFromParams && tabs.includes(activeTabFromParams)) {
            return activeTabFromParams;
        }
        return tabs[0];
    }, [tabs, activeTabFromParams]);

    useEffect(() => {
        ReportTemplateClient.fetchReportApplicationTemplates().then(setReportApplicationTemplate);
    }, []);

    useEffect(() => {
        if (tabs.length === 0 || !activeTab) return;
        if (activeTabFromParams === activeTab) return;

        setSearchParams(
            (prev) => {
                const search = new URLSearchParams(prev);
                search.set('report-tag', activeTab);
                return search.toString();
            },
            { replace: true }
        );
    }, [tabs, activeTab, activeTabFromParams, setSearchParams]);

    const onChange = (filter: string) => {
        setSearchParams((prev) => {
            const search = new URLSearchParams(prev);
            search.set('report-tag', filter.toLowerCase());
            return search.toString();
        });
    };

    useEffect(() => {
        if (!activeTab || !reportApplicationTemplate) return;
        dispatch(refresh({ listId: reportListId, refresh: true }));
    }, [activeTab, reportApplicationTemplate, dispatch]);

    return {
        currentAccount,
        dispatch,
        navigate,
        location,
        tabs,
        showTabs,
        activeTab,
        onChange,
        reportApplicationTemplate,
        reportListId,
        openModal,
        closeModal,
        createReportId,
        isReady: reportApplicationTemplate !== null
    };
};

export default useReportsOverview;
